import {
  OperationStatusLabel,
  ReceivedInvoicesSearchQuery,
  UploadLabel,
  UploadType,
  UploadTypeDisplayPriority,
} from '@/context/services/invoice/type';
import { DEFAULT_RECEIVED_INVOICE_OPERATION_STATUS_IN } from '@/features/invoices/components/List/Search/helper';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { DATE_FORMAT, format } from 'date-util';

export const MAX_VISIBLE_APPLIED_SEARCH_TAGS = 3;
export const APPLIED_SEARCH_TAG_MAX_WIDTH_PX = 304;

const VALUE_SEPARATOR = '、';

const OPERATION_STATUS_DISPLAY_ORDER = [
  'not_started',
  'applying',
  'applied',
  'archived',
  'ocr_processing',
] as const;

const FULL_OPERATION_STATUSES = [
  'not_started',
  'applying',
  'applied',
  'archived',
] as const;

export type AppliedSearchTag = {
  fieldLabel: string;
  value: string;
  expandFully: boolean;
};

export type AppliedSearchTagLabels = {
  keyword: string;
  payee: string;
  amount: string;
  pic: string;
  dueDate: string;
  uploadedAt: string;
  uploadType: string;
  operationStatus: string;
  reconciliation: string;
  reconciliationStatus: Record<string, string>;
};

type BuildAppliedSearchTagsArgs = {
  searchQuery: ReceivedInvoicesSearchQuery;
  labels: AppliedSearchTagLabels;
  payeeNames: Record<string, string>;
  picNames: Record<string, string>;
};

const sameMembers = (
  left: readonly string[],
  right: readonly string[]
): boolean => {
  if (left.length !== right.length) return false;
  const sortedLeft = [...left].sort((a, b) => a.localeCompare(b));
  const sortedRight = [...right].sort((a, b) => a.localeCompare(b));
  return sortedLeft.every((value, index) => value === sortedRight[index]);
};

const isUploadType = (value: string): value is UploadType =>
  Object.prototype.hasOwnProperty.call(UploadLabel, value);

const uploadTypeLabel = (value: string): string =>
  isUploadType(value) ? UploadLabel[value] : value;

const formatDate = (value: string): string => format(value, DATE_FORMAT);

const formatRange = (
  from: string | undefined,
  to: string | undefined,
  formatValue: (value: string) => string = (value) => value
): string | undefined => {
  const left = from ? formatValue(from) : '';
  const right = to ? formatValue(to) : '';
  if (left && right) return `${left} ~ ${right}`;
  if (left) return `${left} ~`;
  if (right) return `~ ${right}`;
  return undefined;
};

const labelsInOrder = (
  values: readonly string[] | undefined,
  order: readonly string[],
  labelFor: (value: string) => string
): string | undefined => {
  if (!values?.length) return undefined;
  const rank = new Map(order.map((value, index) => [value, index]));
  const labels = [...values]
    .sort(
      (left, right) =>
        (rank.get(left) ?? order.length) - (rank.get(right) ?? order.length)
    )
    .map(labelFor)
    .filter(Boolean);
  if (!labels.length) return undefined;
  return labels.join(VALUE_SEPARATOR);
};

const namesFor = (
  ids: readonly string[] | undefined,
  lookup: Record<string, string>
): string | undefined => {
  if (!ids?.length) return undefined;
  const names = ids.map((id) => lookup[id] || id).filter(Boolean);
  if (!names.length) return undefined;
  return names.join(VALUE_SEPARATOR);
};

const pushTagIfDefined = (
  tags: AppliedSearchTag[],
  fieldLabel: string,
  value: string | undefined,
  expandFully = false
) => {
  if (!value) return;
  tags.push({ fieldLabel, value, expandFully });
};

export const buildReceivedInvoiceAppliedSearchTags = ({
  searchQuery,
  labels,
  payeeNames,
  picNames,
}: BuildAppliedSearchTagsArgs): AppliedSearchTag[] => {
  const tags: AppliedSearchTag[] = [];
  const operationStatuses = searchQuery.operation_status_in ?? [];
  const showsOperationStatus =
    operationStatuses.length > 0 &&
    !sameMembers(
      operationStatuses,
      DEFAULT_RECEIVED_INVOICE_OPERATION_STATUS_IN
    );

  pushTagIfDefined(
    tags,
    labels.keyword,
    searchQuery.searchable_number_or_filename_cont?.trim()
  );
  pushTagIfDefined(
    tags,
    labels.payee,
    namesFor(searchQuery.ap_payee_id_in, payeeNames)
  );
  pushTagIfDefined(
    tags,
    labels.amount,
    formatRange(
      searchQuery.total_amount_from,
      searchQuery.total_amount_to,
      toCommaSeparated
    )
  );
  pushTagIfDefined(
    tags,
    labels.uploadType,
    labelsInOrder(
      searchQuery.upload_type_in,
      Object.entries(UploadTypeDisplayPriority)
        .sort(([, left], [, right]) => left - right)
        .map(([value]) => value),
      uploadTypeLabel
    )
  );
  pushTagIfDefined(tags, labels.pic, namesFor(searchQuery.pic_id_in, picNames));
  pushTagIfDefined(
    tags,
    labels.reconciliation,
    labelsInOrder(
      searchQuery.reconciliation_status_in,
      Object.keys(labels.reconciliationStatus),
      (value) => labels.reconciliationStatus[value] || value
    )
  );
  pushTagIfDefined(
    tags,
    labels.dueDate,
    formatRange(searchQuery.due_date_from, searchQuery.due_date_to, formatDate)
  );
  pushTagIfDefined(
    tags,
    labels.uploadedAt,
    formatRange(
      searchQuery.uploaded_at_gteq,
      searchQuery.uploaded_at_lteq,
      formatDate
    )
  );
  pushTagIfDefined(
    tags,
    labels.operationStatus,
    showsOperationStatus
      ? labelsInOrder(
          operationStatuses,
          OPERATION_STATUS_DISPLAY_ORDER,
          (value) => OperationStatusLabel[value] || value
        )
      : undefined,
    sameMembers(operationStatuses, FULL_OPERATION_STATUSES)
  );

  return tags;
};
